/**
 * ProjectEditDialog — 模型选择 Tab
 */
import React from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { ModelConfigItem } from '../types';

interface ModelSelectionTabProps {
  modelsLoading: boolean;
  availableModels: ModelConfigItem[];
  projectModelId: number | null;
  userDefaultModelId: number | null;
  userDefaultModelName: string;
  setProjectModelId: (id: number | null) => void;
}

export default function ModelSelectionTab({
  modelsLoading, availableModels, projectModelId,
  userDefaultModelId, userDefaultModelName, setProjectModelId,
}: ModelSelectionTabProps) {
  const isUsingUserDefault = projectModelId === null;

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500">
        为当前项目指定大模型。所有 AI 生成任务（大纲、知识库检索、章节生成）将使用所选模型。
      </div>
      {modelsLoading ? (
        <div className="flex items-center justify-center py-8 text-gray-400 text-sm">
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mr-2" />
          加载模型列表...
        </div>
      ) : (
        <div className="space-y-3">
          <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${isUsingUserDefault ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
            <input type="radio" name="projectModel" checked={isUsingUserDefault} onChange={() => setProjectModelId(null)}
              className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">
                  使用我的默认模型{userDefaultModelName ? `（${userDefaultModelName}）` : ''}
                </span>
                {userDefaultModelId && (
                  <span className="text-xs text-blue-500 bg-blue-100 px-1.5 py-0.5 rounded">当前默认</span>
                )}
              </div>
              <p className="text-xs text-gray-400 mt-0.5">与"我的设置 → 模型选择"中的默认模型保持一致</p>
            </div>
          </label>
          <div className="text-sm font-medium text-gray-700">或选择项目专用模型：</div>
          {availableModels.length === 0 ? (
            <p className="text-xs text-gray-400 py-2">暂无可用的模型配置，请联系管理员添加</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {availableModels.map((m) => {
                const isSelected = projectModelId === m.id;
                return (
                  <label key={m.id} className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${isSelected ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="projectModel" checked={isSelected} onChange={() => setProjectModelId(m.id)}
                      className="mt-0.5 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{m.name}</span>
                        {m.is_default && <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">系统默认</span>}
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5 font-mono">{m.model}</p>
                    </div>
                    {isSelected && <CheckCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />}
                  </label>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
